(() => {
  if (window.__ynCartV2Ready) return;
  window.__ynCartV2Ready = true;

  let lastCartTrigger = null;
  let pendingQuantityFocus = null;
  const busyTimers = new WeakMap();
  const drawerNoteStates = new WeakMap();
  let activeDrawerNoteState = null;

  const isPolish = () => (document.documentElement.lang || '').toLowerCase().startsWith('pl');

  const getFocusable = (root) => Array.from(root.querySelectorAll(
    'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), details > summary, [tabindex]:not([tabindex="-1"])'
  )).filter((element) => element.getClientRects().length > 0 && !element.closest('[hidden]'));

  const isPopoverOpen = (drawer) => {
    try {
      return drawer.matches(':popover-open');
    } catch (_error) {
      return drawer.hasAttribute('open');
    }
  };

  const getStatusCopy = () => isPolish() ? 'Aktualizuję koszyk...' : 'Updating cart...';

  const getDrawerNoteCopy = () => isPolish()
    ? {
        saving: 'Zapisuję notatkę...',
        saved: 'Notatka została zapisana.',
        error: 'Nie udało się zapisać notatki. Przycisk płatności poniżej nadal ją przekaże.'
      }
    : {
        saving: 'Saving note...',
        saved: 'Your note has been saved.',
        error: 'The note could not be saved. The checkout button below will still submit it.'
      };

  const getDrawerNoteState = (input) => {
    let state = drawerNoteStates.get(input);
    if (state) {
      if (input.isConnected) state.input = input;
      return state;
    }

    if (activeDrawerNoteState) {
      state = activeDrawerNoteState;
      state.input = input;
      input.value = state.desiredValue;
      input.dataset.originalNote = state.savedValue;
      drawerNoteStates.set(input, state);
      return state;
    }

    const originalValue = input.dataset.originalNote ?? input.value;
    state = {
      input,
      savedValue: originalValue,
      desiredValue: input.value,
      timer: 0,
      flushPromise: null,
      touched: false,
      status: 'idle'
    };
    drawerNoteStates.set(input, state);
    activeDrawerNoteState = state;
    return state;
  };

  const setDrawerNoteExpressBlocked = (input, blocked) => {
    const surface = input.closest('#mini-cart');
    const expressGroup = surface?.querySelector('.yn-cart-fast-pay');
    if (!expressGroup) return;

    if (blocked) {
      expressGroup.dataset.ynNoteBlocked = 'true';
      expressGroup.setAttribute('aria-disabled', 'true');
      expressGroup.setAttribute('inert', '');
    } else {
      delete expressGroup.dataset.ynNoteBlocked;
      expressGroup.removeAttribute('inert');
      if (surface.getAttribute('aria-busy') !== 'true') {
        expressGroup.removeAttribute('aria-disabled');
      }
    }
  };

  const renderDrawerNoteState = (input, status) => {
    const state = getDrawerNoteState(input);
    const currentInput = state.input?.isConnected ? state.input : input;
    const details = currentInput.closest('[data-yn-drawer-note]');
    const output = details?.querySelector('[data-yn-drawer-note-status]');
    const copy = getDrawerNoteCopy();
    state.status = status;

    if (details && details.dataset.noteState !== status) details.dataset.noteState = status;
    if (status === 'saving') currentInput.setAttribute('aria-busy', 'true');
    else currentInput.removeAttribute('aria-busy');

    if (output) {
      let message = '';
      if (status === 'saving') message = copy.saving;
      else if (status === 'saved' && state.touched) message = copy.saved;
      else if (status === 'error') message = copy.error;
      if (output.textContent !== message) output.textContent = message;
    }

    setDrawerNoteExpressBlocked(
      currentInput,
      status === 'saving' || status === 'error' || state.desiredValue !== state.savedValue
    );
  };

  const drawerNoteRoute = () => {
    const root = window.Shopify?.routes?.root || '/';
    return `${root.replace(/\/+$/, '')}/cart/update.js`;
  };

  const flushDrawerNote = (input) => {
    const state = getDrawerNoteState(input);
    window.clearTimeout(state.timer);
    state.timer = 0;
    state.desiredValue = input.value;

    if (state.flushPromise) return state.flushPromise;
    if (state.desiredValue === state.savedValue) {
      renderDrawerNoteState(input, state.touched ? 'saved' : 'idle');
      return Promise.resolve();
    }

    state.flushPromise = (async () => {
      while (state.desiredValue !== state.savedValue) {
        const valueToSave = state.desiredValue;
        renderDrawerNoteState(input, 'saving');

        const response = await fetch(drawerNoteRoute(), {
          method: 'POST',
          credentials: 'same-origin',
          keepalive: true,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ note: valueToSave })
        });

        if (!response.ok) throw new Error('Cart note update failed');
        await response.json();
        state.savedValue = valueToSave;
        const currentInput = state.input?.isConnected ? state.input : input;
        currentInput.dataset.originalNote = valueToSave;
        document.querySelectorAll('.cart-page .yn-cart-note textarea[name="note"]').forEach((pageNote) => {
          if (pageNote.value === valueToSave) pageNote.dataset.originalNote = valueToSave;
        });
      }

      renderDrawerNoteState(state.input || input, 'saved');
    })().catch((error) => {
      renderDrawerNoteState(state.input || input, 'error');
      throw error;
    }).finally(() => {
      state.flushPromise = null;
    });

    return state.flushPromise;
  };

  const scheduleDrawerNoteSave = (input) => {
    const state = getDrawerNoteState(input);
    state.touched = true;
    state.desiredValue = input.value;
    window.clearTimeout(state.timer);

    if (state.desiredValue === state.savedValue && !state.flushPromise) {
      renderDrawerNoteState(input, 'saved');
      return;
    }

    renderDrawerNoteState(input, 'saving');
    state.timer = window.setTimeout(() => {
      state.timer = 0;
      flushDrawerNote(input).catch((error) => console.error(error));
    }, 250);
  };

  const isSurfaceActive = (surface) => {
    if (surface.hidden || surface.closest('[hidden]')) return false;
    const drawer = document.querySelector('#slideout-ajax-cart');
    if (surface.id === 'mini-cart') return !!drawer && isPopoverOpen(drawer);
    return !drawer || !isPopoverOpen(drawer);
  };

  const setLiveModes = () => {
    document.querySelectorAll('#mini-cart, .cart-page').forEach((surface) => {
      const live = surface.querySelector('[data-yn-cart-live]');
      if (live) live.setAttribute('aria-live', isSurfaceActive(surface) ? 'polite' : 'off');
    });
  };

  const syncRenderedVisibility = (surface) => {
    surface.querySelectorAll('.js-cart-empty, .js-cart-form').forEach((region) => {
      const isHidden = region.classList.contains('hide');
      region.hidden = isHidden;
      if (isHidden) region.setAttribute('aria-hidden', 'true');
      else region.removeAttribute('aria-hidden');
    });
  };

  const syncCartPageWallet = () => {
    if (!document.body.classList.contains('template-cart')) return;

    const drawer = document.querySelector('#slideout-ajax-cart');
    const targetGroup = drawer && isPopoverOpen(drawer)
      ? document.querySelector('#mini-cart .yn-cart-fast-pay')
      : document.querySelector('.cart-page .yn-cart-fast-pay');
    const targetSlot = targetGroup?.querySelector('.ajax-cart__buttons-additional');
    let walletHosts = Array.from(document.querySelectorAll(
      '.cart-page .yn-cart-fast-pay shopify-accelerated-checkout-cart,' +
      '#mini-cart .yn-cart-fast-pay shopify-accelerated-checkout-cart'
    ));

    if (!walletHosts.length) {
      walletHosts = Array.from(document.querySelectorAll(
        '.cart-page .yn-cart-fast-pay shopify-accelerated-checkout,' +
        '#mini-cart .yn-cart-fast-pay shopify-accelerated-checkout'
      ));
    }

    const getWalletNode = (host) => {
      const slot = host?.closest('.ajax-cart__buttons-additional');
      let node = host;
      while (node?.parentElement && node.parentElement !== slot) node = node.parentElement;
      return node;
    };

    const walletNode = getWalletNode(walletHosts.shift());
    if (targetSlot && walletNode && walletNode.parentElement !== targetSlot) targetSlot.append(walletNode);
    walletHosts.forEach((duplicate) => getWalletNode(duplicate)?.remove());
  };

  const setMutationControlsDisabled = (surface, disabled) => {
    surface.querySelectorAll(
      '[data-ajax-qty-decrease], [data-ajax-qty-increase], [data-item-qty], .js-cart-remove, [data-yn-drawer-note-input], button[name="checkout"]'
    ).forEach((control) => {
      control.disabled = disabled;
    });

    surface.querySelectorAll('.yn-cart-fast-pay').forEach((group) => {
      if (disabled) group.setAttribute('aria-disabled', 'true');
      else if (group.dataset.ynNoteBlocked !== 'true') group.removeAttribute('aria-disabled');
    });
  };

  const syncQuantityControlStates = (surface) => {
    if (!surface || surface.getAttribute('aria-busy') === 'true') return;

    surface.querySelectorAll('[data-item-qty]').forEach((quantity) => {
      const group = quantity.closest('.ajax-cart__item-quantity');
      const decrease = group?.querySelector('[data-ajax-qty-decrease]');
      const increase = group?.querySelector('[data-ajax-qty-increase]');
      const value = Number(quantity.value);
      const declaredMinimum = quantity.min === '' ? 1 : Number(quantity.min);
      const minimum = Number.isFinite(declaredMinimum) ? Math.max(1, declaredMinimum) : 1;
      const maximum = quantity.max === '' ? Infinity : Number(quantity.max);

      quantity.min = String(minimum);

      if (decrease) decrease.disabled = Number.isFinite(value) && value <= minimum;
      if (increase) increase.disabled = Number.isFinite(value) && value >= maximum;
    });
  };

  const releasePendingQuantityFocus = (surface, restoreValue = false) => {
    if (pendingQuantityFocus?.surface !== surface) return;

    const { itemKey, direction, previousValue } = pendingQuantityFocus;
    pendingQuantityFocus = null;
    const quantity = Array.from(surface.querySelectorAll('[data-item-qty]'))
      .find((element) => element.dataset.itemKey === itemKey);

    if (restoreValue && quantity && Number.isFinite(previousValue)) {
      quantity.value = String(previousValue);
      quantity.setAttribute('value', String(previousValue));
    }

    requestAnimationFrame(() => {
      const selector = direction === 'increase'
        ? '[data-ajax-qty-increase]'
        : '[data-ajax-qty-decrease]';
      const nextControl = Array.from(surface.querySelectorAll(selector))
        .find((element) => element.dataset.itemKey === itemKey);
      const focusTarget = nextControl && !nextControl.disabled ? nextControl : quantity;
      focusTarget?.focus({ preventScroll: true });
    });
  };

  const markSurfaceBusy = (surface) => {
    if (!surface) return;
    window.clearTimeout(busyTimers.get(surface));
    surface.setAttribute('aria-busy', 'true');
    setMutationControlsDisabled(surface, true);
    const live = surface.querySelector('[data-yn-cart-live]');
    if (live && isSurfaceActive(surface)) live.textContent = getStatusCopy();
    busyTimers.set(surface, window.setTimeout(() => {
      if (surface.getAttribute('aria-busy') !== 'true') return;
      surface.removeAttribute('aria-busy');
      releasePendingQuantityFocus(surface, true);
      setMutationControlsDisabled(surface, false);
      syncQuantityControlStates(surface);
      if (live && isSurfaceActive(surface)) {
        live.textContent = isPolish()
          ? 'Nie udało się zaktualizować koszyka. Spróbuj ponownie.'
          : 'The cart could not be updated. Please try again.';
      }
    }, 12000));
  };

  const describeCart = (scope) => {
    const count = scope.querySelector('.js-cart-count')?.textContent.trim();
    const total = scope.querySelector('[data-yn-cart-total]')?.textContent.trim();
    if (!count) return '';
    const numericCount = Number(count.replace(/[^0-9]/g, ''));
    if (numericCount === 0) return isPolish() ? 'Koszyk jest pusty.' : 'Your cart is empty.';
    if (isPolish()) return `Koszyk zaktualizowany. ${count} szt. Razem ${total || ''}.`;
    return `Cart updated. ${count} items. Total ${total || ''}.`;
  };

  const initializeSurface = (surface) => {
    if (!(surface instanceof Element) || surface.dataset.ynCartV2Ready === 'true') return;
    surface.dataset.ynCartV2Ready = 'true';

    if (
      surface.classList.contains('cart-page') &&
      document.querySelector('body.template-cart .yn-empty-cart')
    ) {
      const section = surface.closest('.shopify-section') || surface;
      section.hidden = true;
      section.setAttribute('aria-hidden', 'true');
      surface.querySelector('[data-yn-cart-live]')?.setAttribute('aria-live', 'off');
      return;
    }

    const live = surface.querySelector('[data-yn-cart-live]');
    let lastSignature = '';
    let removalPending = false;
    let announceTimer = 0;

    const refresh = () => {
      syncRenderedVisibility(surface);

      if (surface.id === 'mini-cart') {
        const info = surface.querySelector('.yn-cart-page-info');
        info?.querySelector('.yn-cart-note')?.remove();
        if (info && !info.children.length) info.remove();

        const drawerNoteInput = surface.querySelector('[data-yn-drawer-note-input]');
        if (drawerNoteInput) {
          drawerNoteInput.name = 'note';
          const state = getDrawerNoteState(drawerNoteInput);
          renderDrawerNoteState(drawerNoteInput, state.status);
        }
      } else if (surface.classList.contains('cart-page')) {
        surface.querySelector('[data-yn-drawer-note]')?.remove();

        const items = surface.querySelector('.ajax-cart__cart-items');
        const info = surface.querySelector('.yn-cart-page-info');
        if (items && info && info.parentElement !== items) items.append(info);

        const note = info?.querySelector('textarea[name="note"]');
        if (note && !note.placeholder) {
          note.placeholder = isPolish()
            ? 'Informacja dla obsługi zamówienia, opcjonalnie'
            : 'Optional information for the order team';
        }
      }

      surface.querySelectorAll('.ajax-cart__item-image img').forEach((image) => {
        image.setAttribute('sizes', '(max-width: 749px) 80px, (max-width: 1100px) 96px, 144px');
      });

      const signature = describeCart(surface);
      if (!lastSignature) {
        lastSignature = signature;
      } else if (signature && signature !== lastSignature) {
        const becameEmpty = signature === (isPolish() ? 'Koszyk jest pusty.' : 'Your cart is empty.');
        lastSignature = signature;
        window.clearTimeout(announceTimer);
        announceTimer = window.setTimeout(() => {
          if (live && isSurfaceActive(surface)) live.textContent = signature;
        }, 180);

        if (becameEmpty) {
          removalPending = false;
          requestAnimationFrame(() => {
            const emptyTitle = surface.querySelector('[data-yn-cart-empty-title]');
            if (emptyTitle instanceof HTMLElement && emptyTitle.getClientRects().length > 0) {
              emptyTitle.focus({ preventScroll: true });
            }
          });
        }

        window.clearTimeout(busyTimers.get(surface));
        busyTimers.delete(surface);
        surface.removeAttribute('aria-busy');
        setMutationControlsDisabled(surface, false);
        syncQuantityControlStates(surface);

        releasePendingQuantityFocus(surface);
      }

      if (removalPending) {
        removalPending = false;
        requestAnimationFrame(() => {
          const nextControl = surface.querySelector('.js-cart-remove, button[name="checkout"], .slideout__trigger-ajax-cart');
          nextControl?.focus({ preventScroll: true });
        });
      }

      syncQuantityControlStates(surface);
      syncCartPageWallet();
    };

    surface.addEventListener('click', (event) => {
      if (event.target.closest('.js-cart-remove')) {
        removalPending = true;
        markSurfaceBusy(surface);
      }
    }, true);

    surface.addEventListener('input', (event) => {
      const input = event.target.closest('[data-yn-drawer-note-input]');
      if (input && surface.id === 'mini-cart') {
        scheduleDrawerNoteSave(input);
        return;
      }

      const pageNote = event.target.closest('.yn-cart-note textarea[name="note"]');
      const drawerNoteInput = document.querySelector('#mini-cart [data-yn-drawer-note-input]');
      if (!pageNote || !drawerNoteInput || !surface.classList.contains('cart-page')) return;

      drawerNoteInput.value = pageNote.value;
      scheduleDrawerNoteSave(drawerNoteInput);
    });

    surface.addEventListener('focusout', (event) => {
      const input = event.target.closest('[data-yn-drawer-note-input]');
      if (!input || surface.id !== 'mini-cart') return;
      flushDrawerNote(input).catch((error) => console.error(error));
    });

    if ('MutationObserver' in window) {
      new MutationObserver(refresh).observe(surface, {
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    refresh();
  };

  const initializeDrawer = () => {
    const drawer = document.querySelector('#slideout-ajax-cart');
    const miniCart = drawer?.querySelector('#mini-cart');
    if (!drawer || !miniCart || drawer.dataset.ynCartDrawerV2Ready === 'true') return;

    drawer.dataset.ynCartDrawerV2Ready = 'true';

    drawer.addEventListener('toggle', () => {
      setLiveModes();
      syncCartPageWallet();
      if (isPopoverOpen(drawer)) {
        if (!lastCartTrigger?.isConnected && document.activeElement instanceof HTMLElement) {
          lastCartTrigger = document.activeElement;
        }
        requestAnimationFrame(() => {
          drawer.querySelector('.slideout__trigger-ajax-cart')?.focus({ preventScroll: true });
        });
      } else if (lastCartTrigger?.isConnected) {
        requestAnimationFrame(() => lastCartTrigger.focus({ preventScroll: true }));
      }
    });

    drawer.addEventListener('keydown', (event) => {
      if (event.key !== 'Tab' || !isPopoverOpen(drawer)) return;
      const focusable = getFocusable(drawer);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initialize = (scope = document) => {
    scope.querySelectorAll?.('#mini-cart, .cart-page').forEach(initializeSurface);
    if (scope.matches?.('#mini-cart, .cart-page')) initializeSurface(scope);
    initializeDrawer();
    setLiveModes();
    syncCartPageWallet();
  };

  const keepFocusedControlVisible = (target) => {
    if (!(target instanceof Element) || !window.matchMedia('(max-width: 600px)').matches) return;

    const page = target.closest('.cart-page');
    const actions = page?.querySelector('.yn-cart-purchase-actions');
    if (!page || !actions || target.closest('.yn-cart-purchase-actions')) return;

    requestAnimationFrame(() => {
      if (document.activeElement !== target || !target.getClientRects().length) return;

      const actionStyle = window.getComputedStyle(actions);
      if (actionStyle.position !== 'fixed' || !actions.getClientRects().length) return;

      const targetRect = target.getBoundingClientRect();
      const actionRect = actions.getBoundingClientRect();
      const clearance = 12;

      if (targetRect.bottom > actionRect.top - clearance && targetRect.top < actionRect.bottom) {
        window.scrollBy({
          top: targetRect.bottom - actionRect.top + clearance,
          left: 0,
          behavior: 'auto'
        });
      }
    });
  };

  const getPendingDrawerNoteInput = (surface) => {
    if (!surface || surface.id !== 'mini-cart') return null;

    const input = surface.querySelector('[data-yn-drawer-note-input]');
    if (!input) return null;

    const state = getDrawerNoteState(input);
    state.desiredValue = input.value;
    return state.flushPromise || state.desiredValue !== state.savedValue ? input : null;
  };

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const control = event.target.closest(
      '[data-ajax-qty-decrease], [data-ajax-qty-increase], .js-cart-remove'
    );
    const surface = control?.closest('#mini-cart');
    if (!control || !surface) return;

    if (control.dataset.ynNoteReplay === 'true') {
      delete control.dataset.ynNoteReplay;
      return;
    }

    const noteInput = getPendingDrawerNoteInput(surface);
    if (!noteInput) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    flushDrawerNote(noteInput).then(() => {
      if (!control.isConnected) return;
      control.dataset.ynNoteReplay = 'true';
      control.click();
    }).catch((error) => console.error(error));
  }, true);

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof Element)) return;

    const quantity = event.target.closest('[data-item-qty]');
    const surface = quantity?.closest('#mini-cart');
    if (!quantity || !surface) return;

    if (quantity.dataset.ynNoteReplay === 'true') {
      delete quantity.dataset.ynNoteReplay;
      return;
    }

    const noteInput = getPendingDrawerNoteInput(surface);
    if (!noteInput) return;

    const intendedValue = quantity.value;
    event.preventDefault();
    event.stopImmediatePropagation();

    flushDrawerNote(noteInput).then(() => {
      if (!quantity.isConnected) return;
      quantity.value = intendedValue;
      quantity.dataset.ynNoteReplay = 'true';
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
    }).catch((error) => console.error(error));
  }, true);

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-ajax-qty-decrease], [data-ajax-qty-increase]');
    if (!control || !control.closest('#mini-cart, .cart-page')) return;

    const quantity = control.closest('.ajax-cart__item-quantity')?.querySelector('[data-item-qty]');
    if (!quantity) return;

    const surface = control.closest('#mini-cart, .cart-page');
    const currentQuantity = Number(quantity.value);
    const declaredMinimum = quantity.min === '' ? 1 : Number(quantity.min);
    const minimum = Number.isFinite(declaredMinimum) ? Math.max(1, declaredMinimum) : 1;
    const maximum = quantity.max === '' ? Infinity : Number(quantity.max);
    const isIncrease = control.matches('[data-ajax-qty-increase]');

    if (
      !Number.isFinite(currentQuantity) ||
      (!isIncrease && currentQuantity <= minimum) ||
      (isIncrease && currentQuantity >= maximum)
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
      syncQuantityControlStates(surface);
      return;
    }

    pendingQuantityFocus = {
      surface,
      itemKey: control.dataset.itemKey,
      direction: isIncrease ? 'increase' : 'decrease',
      previousValue: currentQuantity
    };
    event.preventDefault();
    event.stopImmediatePropagation();

    if (isIncrease) {
      quantity.stepUp(1);
    } else {
      quantity.stepDown(1);
    }
    quantity.dispatchEvent(new Event('change', { bubbles: true }));
    markSurfaceBusy(surface);
  }, true);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('.js-mini-cart-trigger, [aria-controls="slideout-ajax-cart"]');
    if (trigger && !trigger.closest('#slideout-ajax-cart')) lastCartTrigger = trigger;
  }, true);

  document.addEventListener('submit', (event) => {
    const form = event.target.closest('#mini-cart form');
    const checkoutButton = event.submitter?.matches('button[name="checkout"]')
      ? event.submitter
      : null;
    if (!form || !checkoutButton) return;

    if (form.dataset.ynDrawerNoteCheckoutReady === 'true') {
      delete form.dataset.ynDrawerNoteCheckoutReady;
      return;
    }

    const input = form.querySelector('[data-yn-drawer-note-input]');
    if (!input) return;
    const state = getDrawerNoteState(input);
    state.desiredValue = input.value;
    if (!state.flushPromise && state.desiredValue === state.savedValue) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    checkoutButton.disabled = true;
    checkoutButton.setAttribute('aria-busy', 'true');

    flushDrawerNote(input).catch((error) => {
      console.error(error);
    }).finally(() => {
      checkoutButton.disabled = false;
      checkoutButton.removeAttribute('aria-busy');
      form.dataset.ynDrawerNoteCheckoutReady = 'true';

      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit(checkoutButton);
        return;
      }

      const checkoutFallback = document.createElement('input');
      checkoutFallback.type = 'hidden';
      checkoutFallback.name = 'checkout';
      checkoutFallback.value = 'checkout';
      form.append(checkoutFallback);
      HTMLFormElement.prototype.submit.call(form);
    });
  }, true);

  document.addEventListener('focusin', (event) => keepFocusedControlVisible(event.target));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
